import { AfterContentChecked, Directive, EventEmitter, Host, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import {
  DataBindingDirective,
  GridComponent,
  GridDataResult,
} from '@progress/kendo-angular-grid';
import {
  AggregateDescriptor,
  CompositeFilterDescriptor,
  FilterDescriptor,
  DataSourceRequestState,
  GroupDescriptor,
  SortDescriptor,
  State,
  toDataSourceRequest,
  translateDataSourceResultGroups,
} from '@progress/kendo-data-query';
import { catchError, debounceTime, map, merge, Observable, of, share, Subject, Subscription, switchMap, tap } from 'rxjs';
import { KendoGridStateDirective } from '../kendo-grid-state/kendo-grid-state.directive';
import {
  IDataSourceResult,
  IWebApiDataSourceRequest,
  IMvcDataSourceBase,
  IMvcGridDataResult,
  IMvcAggregateResult,
} from '@ticomix/common';

/**
 * Interface that defines an MVC data source with an action method to fetch data.
 * Extends the base data source interface with a specific action method for grid data.
 * 
 * @template T The type of data items returned by the data source.
 */
export interface IMvcDataSource<T = any> extends IMvcDataSourceBase<T> {
  /**
   * Fetches data from the server based on the provided request and state.
   * 
   * @param request The WebAPI data source request containing filtering, sorting, and paging parameters.
   * @param state Optional state object containing additional information about the grid state.
   * @returns Observable that resolves to a data source result containing the requested data.
   */
  action: (
    request: IWebApiDataSourceRequest,
    state?: State
  ) => Observable<IDataSourceResult<T>>;
}

/**
 * A directive that provides MVC-style data binding for Kendo UI Grid components.
 * 
 * This directive extends the standard Kendo DataBindingDirective to provide seamless
 * integration with ASP.NET MVC/WebAPI backends. It handles data fetching, paging,
 * sorting, filtering, and grouping operations, translating between Kendo UI's client-side
 * data structures and server-side request/response formats.
 * 
 * Key features:
 * - Automatic handling of MVC-style data requests and responses
 * - Support for server-side aggregates
 * - Virtual scrolling support with groupPaging=true for large datasets
 * - Backward compatibility mode for older server implementations
 * - Integration with KendoGridStateDirective for state persistence
 * - Efficient caching of fetched data to minimize server requests
 * - Smart error detection for compatibility issues
 * 
 * @example
 * ```html
 * <!-- Modern .NET 8+ backend with virtual scrolling -->
 * <kendo-grid [mvcDataSource]="productDataSource" [pageable]="true" [sortable]="true">
 *   <kendo-grid-column field="ProductName" title="Product Name"></kendo-grid-column>
 *   <kendo-grid-column field="UnitPrice" title="Price"></kendo-grid-column>
 * </kendo-grid>
 * 
 * <!-- Older .NET 6 backend compatibility -->
 * <kendo-grid [mvcDataSource]="productDataSource" [usePageSizePaging]="true" [pageable]="true">
 *   <kendo-grid-column field="ProductName" title="Product Name"></kendo-grid-column>
 *   <kendo-grid-column field="UnitPrice" title="Price"></kendo-grid-column>
 * </kendo-grid>
 * ```
 * 
 * ```typescript
 * @Component({...})
 * export class ProductListComponent {
 *   public productDataSource: IMvcDataSource = {
 *     action: (request) => this.http.post<IDataSourceResult>('/api/products', request)
 *   };
 * }
 * ```
 */
@Directive({
  selector: '[mvcDataSource]',
  standalone: true,
})
export class KendoDataSourceMvcDirective extends DataBindingDirective implements OnInit, OnDestroy, AfterContentChecked {
  /** @internal Default page size for pagination. */
  private static readonly DEFAULT_PAGE_SIZE = 20;
  /**
   * Creates an instance of the KendoDataSourceMvcDirective.
   * 
   * @param kendoGridStateDirective Optional directive for persisting grid state.
   * @param grid The host grid component.
   */
  constructor(
    @Host()
    @Optional()
    private kendoGridStateDirective: KendoGridStateDirective | null,
    grid: GridComponent
  ) {
    super(grid);
  }

  /** @internal The MVC data source instance. */
  private _dataSource?: IMvcDataSource;

  /**
   * Gets the current data source.
   * 
   * @returns The current MVC data source or undefined if not set.
   */
  get dataSource(): IMvcDataSource | undefined {
    return this._dataSource;
  }

  /**
   * Sets the MVC data source for the grid.
   * This is the primary input that provides the data fetching functionality.
   * 
   * @param dataSource The MVC data source to use for fetching data.
   */
  @Input('mvcDataSource')
  public set dataSource(dataSource: IMvcDataSource) {
    this._dataSource = dataSource;
  }

  /** @internal Local state tracking to avoid race conditions */
  private currentFilter?: CompositeFilterDescriptor;
  private currentSort?: SortDescriptor[];
  private currentGroup?: GroupDescriptor[];

  /** @internal Flag to track if row height has been checked */
  private hasCheckedRowHeight: boolean = false;

  /** @internal Flag to track if paging notice has been logged */
  private hasLoggedPagingNotice: boolean = false;

  /** @internal Observable for the current pending request */
  private pendingRequest$: Observable<IMvcGridDataResult> | null = null;

  /** @internal The current aggregates configuration. */
  private _aggregate: AggregateDescriptor[] = [];

  /**
   * Gets the current aggregates configuration.
   * 
   * @returns The current array of aggregate descriptors.
   */
  get aggregates(): AggregateDescriptor[] {
    return this._aggregate;
  }

  /**
   * Sets the aggregates configuration for the grid.
   * Aggregates are used for calculating summary values like sum, average, etc.
   * 
   * @param aggregate The array of aggregate descriptors defining the aggregations to perform.
   */
  @Input()
  public set aggregates(aggregate: AggregateDescriptor[]) {
    this._aggregate = aggregate;
  }

  /** @internal The current aggregate results from the server. */
  private _aggregateResults: IMvcAggregateResult[] | null = null;

  /**
   * Emits aggregate results when they change.
   * This output can be used to display aggregate values outside the grid.
   */
  @Output() aggregateResultsChange = new EventEmitter<IMvcAggregateResult[] | null>();

  /**
   * Gets the current aggregate results.
   * 
   * @returns The current aggregate results or null if none are available.
   */
  get aggregateResults(): IMvcAggregateResult[] | null {
    return this._aggregateResults;
  }

  /**
   * Emits when a data fetch error occurs.
   * This output can be used to handle errors in the parent component.
   */
  @Output() dataError = new EventEmitter<any>();

  /** @internal The current page/pageSize mode configuration. */
  private _usePageSizePaging: boolean = true;

  /**
   * Gets the current page/pageSize paging mode.
   * 
   * @returns True if using legacy page/pageSize mode, false if using modern skip/take mode.
   */
  get usePageSizePaging(): boolean {
    return this._usePageSizePaging;
  }

  /**
   * Sets the paging mode for server compatibility.
   * 
   * When true (default), uses traditional page/pageSize parameters for compatibility with older
   * server implementations that don't support skip/take virtual scrolling parameters.
   * When false, uses modern skip/take parameters for optimal virtual scrolling.
   * 
   * @param value True to use page/pageSize mode, false to use skip/take mode.
   * 
   * @example
   * ```html
   * <!-- For older .NET 6 backends -->
   * <kendo-grid [mvcDataSource]="dataSource" [usePageSizePaging]="true">
   * 
   * <!-- For modern .NET 8+ backends (default) -->
   * <kendo-grid [mvcDataSource]="dataSource">
   * ```
   */
  @Input()
  public set usePageSizePaging(value: boolean) {
    this._usePageSizePaging = value;
  }

  /**
   * Updates the aggregate results and emits the change event if the value has changed.
   * 
   * @internal
   * @param value The new aggregate results.
   */
  private setAggregateResults(value: IMvcAggregateResult[] | null): void {
    // Only update and emit if the value has actually changed
    // Use efficient comparison instead of JSON.stringify
    if (!this.areAggregatesEqual(this._aggregateResults, value)) {
      this._aggregateResults = value;
      this.aggregateResultsChange.emit(value);
    }
  }

  /**
   * Efficiently compares two aggregate result arrays for equality.
   * 
   * @internal
   * @param a First aggregate results array.
   * @param b Second aggregate results array.
   * @returns True if the arrays are equal, false otherwise.
   */
  private areAggregatesEqual(a: IMvcAggregateResult[] | null, b: IMvcAggregateResult[] | null): boolean {
    if (a === b) return true;
    if (!a || !b || a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
      const aItem = a[i];
      const bItem = b[i];
      if (aItem.Member !== bItem.Member || 
          aItem.Value !== bItem.Value ||
          aItem.ItemCount !== bItem.ItemCount) {
        return false;
      }
    }
    return true;
  }

  /** @internal Collection of all active subscriptions for cleanup. */
  private _subscriptions: Subscription[] = [];
  
  /** @internal Subject to trigger manual rebinding. */
  private rebindTrigger = new Subject<void>();

  /**
   * Maps the server response data to the format expected by the Kendo UI Grid.
   * Handles special processing for grouped data.
   * 
   * @internal
   * @param hasGroups Whether the data contains groups.
   * @param result The raw data source result from the server.
   * @returns A mapped result compatible with the Kendo UI Grid.
   */
  protected mapResult(
    hasGroups: boolean,
    result: IDataSourceResult
  ): IMvcGridDataResult {
    let data = result.Data ?? [];
    let total = result.Total;

    return {
      // If the data is grouped, translate it to a compatible format using the translateDataSourceResultGroups helper.
      data: data && hasGroups ? translateDataSourceResultGroups(data) : data,
      total: total,
      aggregateResults: result.AggregateResults ?? null,
      errors: result.Errors ?? null,
    };
  }

  /** 
   * Cache for fetched page data to minimize server requests.
   * Uses a sparse array approach to prevent memory exhaustion.
   * @internal 
   */
  pageData: any[] = [];
  
  /** 
   * Total record count from the server response.
   * @internal 
   */
  private serverTotal: number = 0;


  /**
   * Creates a self-contained observable for single page data fetching with error handling.
   * 
   * @internal
   * @param request The request parameters for the single page.
   * @param state The current grid state.
   * @param hasGroups Whether the data contains groups.
   * @returns An observable that resolves to the grid data result with built-in error handling.
   */
  private createSinglePageObservable(
    request: IWebApiDataSourceRequest,
    state: State,
    hasGroups: boolean
  ): Observable<IMvcGridDataResult> {
    return this.dataSource!.action(request, state).pipe(
      map(result => this.mapResult(hasGroups, result)),
      catchError(error => {
        console.error('Failed to fetch data:', error);
        
        // Smart error detection for compatibility issues
        if (!this.usePageSizePaging && error.status === 400 && error.error) {
          const errorMessage = JSON.stringify(error.error).toLowerCase();
          const unsupportedKeywords = ['skip', 'take', 'grouppaging', 'datasourcerequest', 'binding'];
          
          // Check for keywords that strongly suggest a model binding failure on the Kendo request object
          if (unsupportedKeywords.some(kw => errorMessage.includes(kw))) {
            console.error(
              `[KendoDataSourceMvcDirective] The data request failed, which may be due to an incompatible backend. ` +
              `If your server does not support skip/take parameters for Kendo DataSourceRequest, ` +
              `try setting [usePageSizePaging]="true" on the directive.`,
              error
            );
          }
        }
        
        this.dataError.emit(error);
        return of({
          data: [],
          total: 0,
          aggregateResults: null,
          errors: null
        });
      })
    );
  }



  /**
   * Function to modify filter expressions before sending to the server.
   * Can be overridden by derived classes to customize filter handling.
   * 
   * @param filter The filter to modify.
   * @returns The modified filter.
   */
  @Input()
  modifyFilter: (filter: CompositeFilterDescriptor | FilterDescriptor) => CompositeFilterDescriptor | FilterDescriptor = (filter) => filter;

  /**
   * Queries the data based on the current grid state.
   * This is the main method that fetches data from the server.
   * 
   * @param stateArg The current grid state including sort, filter, group, etc.
   * @returns An observable that resolves to the grid data result.
   */
  query(stateArg: State): Observable<IMvcGridDataResult> {
    console.log(`KendoDataSourceMvcDirective: query() called with skip=${stateArg.skip}, take=${stateArg.take}`);
    
    if (this.usePageSizePaging) {
      return this.queryLegacy(stateArg);
    } else {
      return this.queryModern(stateArg);
    }
  }

  /**
   * Modern query implementation using skip/take for virtual scrolling.
   * 
   * @param stateArg The current grid state including sort, filter, group, etc.
   * @returns An observable that resolves to the grid data result.
   */
  private queryModern(stateArg: State): Observable<IMvcGridDataResult> {
    if (!this.dataSource) {
      throw new Error('No dataSource provided');
    }

    //Copy state and add aggregate
    let state = { ...stateArg, aggregates: this.aggregates } as DataSourceRequestState;
    
    // Apply filter modifications before converting to request
    if (state.filter) {
      state.filter = this.modifyFilter(state.filter) as CompositeFilterDescriptor;
    }

    let formDataObject = toDataSourceRequest(state);

    // For virtual scrolling with groupPaging=true, we pass skip/take directly to the server
    // without any page alignment. The server will use these values directly via Skip().Take()

    let needsToFetch = false;
    if (this.pageData.length > 0) {
      let start = state.skip ?? 0;
      let end = (state.skip ?? 0) + (state.take ?? this.pageData.length);

      for (let i = start; i < end; i++) {
        if (!this.pageData[i]) {
          needsToFetch = true;
          break;
        }
      }
    } else {
      needsToFetch = true;
    }

    if (needsToFetch) {
      this.grid.loading = true;

      // Calculate parameters for modern mode
      const skip = state.skip ?? 0;
      const take = state.take ?? KendoDataSourceMvcDirective.DEFAULT_PAGE_SIZE;

      // MODERN MODE: Use skip/take for virtual scrolling
      let request: IWebApiDataSourceRequest = {
        sort: formDataObject.sort,
        group: formDataObject.group,
        page: null, // Remove page/pageSize to avoid server-side conflicts
        pageSize: null,
        filter: formDataObject.filter,
        mode: formDataObject.mode,
        aggregate: formDataObject.aggregate,
        groupPaging: true, // Enable skip/take mode on server
        skip: skip,
        take: take,
        includeSubGroupCount: formDataObject.includeSubGroupCount,
      };

      console.debug(`KendoDataSourceMvcDirective: Modern skip/take request: skip=${request.skip}, take=${request.take}, groupPaging=true`);

      const hasGroups = state.group && state.group.length ? true : false;
      
      const fetchOperation = this.createSinglePageObservable(request, state, hasGroups);

      return fetchOperation.pipe(
        map(data => {
          // Store the server total for correct total reporting
          this.serverTotal = data.total;

          // In modern mode, use skip directly
          const skipStart = request.skip ?? 0;

          // Store fetched data in our cache at the correct index
          for (let i = 0; i < data.data.length; i++) {
            const itemIndex = i + skipStart;
            this.pageData[itemIndex] = data.data[i];
          }

          // Update aggregate results if they exist in the response
          if (data.aggregateResults) {
            this.setAggregateResults(data.aggregateResults);
          }

          // For virtual scrolling, we don't pre-allocate the array to the total size
          // to avoid memory allocation issues with large datasets.
          // The array grows organically as data is fetched.

          // Server returned exactly what we requested, so return it directly
          return {
            data: data.data,
            total: this.serverTotal,
            aggregateResults: this.aggregateResults,
            errors: null
          } as IMvcGridDataResult;
        })
      );
    } else {
      // Data is already cached - return a slice of the cache for the current view
      const skipStart = state.skip ?? 0;
      const takeAmount = state.take ?? KendoDataSourceMvcDirective.DEFAULT_PAGE_SIZE;
      console.debug(`KendoDataSourceMvcDirective: Using cached data: skip=${skipStart}, take=${takeAmount}, cache length=${this.pageData.length}`);
      
      const dataSlice = this.pageData.slice(skipStart, skipStart + takeAmount);

      return of({
        total: this.serverTotal, // Use server total, not cache length
        data: dataSlice,
        aggregateResults: this.aggregateResults,
        errors: null
      } as IMvcGridDataResult);
    }
  }

  /**
   * Legacy query implementation using page/pageSize for compatibility with older server implementations.
   * This implementation is based on the working version from commit 8a6a31e.
   * 
   * @param stateArg The current grid state including sort, filter, group, etc.
   * @returns An observable that resolves to the grid data result.
   */
  private queryLegacy(stateArg: State): Observable<IMvcGridDataResult> {
    if (!this.dataSource) {
      throw new Error('No dataSource provided');
    }

    let dataSource = this.dataSource;

    //Copy state and add aggregate
    let state = { ...stateArg, aggregates: this.aggregates } as DataSourceRequestState;
    
    // Apply filter modifications before converting to request
    if (state.filter) {
      state.filter = this.modifyFilter(state.filter) as CompositeFilterDescriptor;
    }

    let formDataObject = toDataSourceRequest(state);
    if (isNaN(formDataObject.page)) {
      formDataObject.page = 1;
    }

    let needsToFetch = false;
    let pageToFetch = formDataObject.page;
    
    if (this.pageData.length > 0) {
      let start = state.skip ?? 0;
      let end = (state.skip ?? 0) + (state.take ?? this.pageData.length);

      // Find the first missing item to determine which page we actually need
      for (let i = start; i < end; i++) {
        if (!this.pageData[i]) {
          needsToFetch = true;
          // Calculate which page contains this missing item
          const serverPageSize = formDataObject.pageSize ?? KendoDataSourceMvcDirective.DEFAULT_PAGE_SIZE;
          pageToFetch = Math.floor(i / serverPageSize) + 1;
          break;
        }
      }
    } else {
      needsToFetch = true;
    }

    if (needsToFetch) {
      // If there's already a pending request for the same data, return it
      if (this.pendingRequest$) {
        console.debug('KendoDataSourceMvcDirective: Reusing pending request');
        return this.pendingRequest$;
      }

      this.grid.loading = true;

      let request = <IWebApiDataSourceRequest>{
        sort: formDataObject.sort,
        group: formDataObject.group,
        page: pageToFetch, // Use the calculated page that contains missing data
        pageSize: formDataObject.pageSize,
        filter: formDataObject.filter,
        mode: formDataObject.mode,
        aggregate: formDataObject.aggregate,
        groupPaging: formDataObject.groupPaging,
        skip: formDataObject.skip,
        take: formDataObject.take,
        includeSubGroupCount: formDataObject.includeSubGroupCount,
      };

      console.debug(`KendoDataSourceMvcDirective: Legacy page/pageSize request: page=${request.page}, pageSize=${request.pageSize}, groupPaging=${request.groupPaging}`);

      const hasGroups = state.group && state.group.length ? true : false;
      
      // Use direct dataSource.action call like the working version
      const fetchOperation = dataSource.action(request, state).pipe(
        map(result => this.mapResult(hasGroups, result)),
        catchError(error => {
          console.error('Failed to fetch legacy data:', error);
          this.dataError.emit(error);
          return of({
            data: [],
            total: 0,
            aggregateResults: null,
            errors: null
          });
        })
      );

      // Create and store the shared observable
      this.pendingRequest$ = fetchOperation.pipe(
        map(data => {
          // Ensure total is a valid number before setting array length
          const total = data.total ?? 0;
          if (total >= 0 && total <= Number.MAX_SAFE_INTEGER) {
            this.pageData.length = total;
          }
          const skipStart = ((request.page ?? 1) - 1) * (request.pageSize ?? KendoDataSourceMvcDirective.DEFAULT_PAGE_SIZE);

          for (let i = 0; i < data.data.length; i++) {
            const itemIndex = i + skipStart;
            this.pageData[itemIndex] = data.data[i];
          }

          // CRITICAL: Return slice of pageData matching the exact skip/take request
          data.data = this.pageData.slice(
            state.skip ?? 0,
            (state.skip ?? 0) + (state.take ?? data.total)
          );
          dataSource.data = this.pageData;

          // Update aggregate results if they exist in the response
          if (data.aggregateResults) {
            this.setAggregateResults(data.aggregateResults);
          }

          return data;
        }),
        tap(() => {
          // Clear the pending request on completion
          this.pendingRequest$ = null;
        }),
        catchError(error => {
          // Clear the pending request on error
          this.pendingRequest$ = null;
          throw error;
        }),
        share() // Share the observable so multiple subscribers get the same result
      );

      return this.pendingRequest$;
    } else {
      let data = <IMvcGridDataResult>{
        total: this.pageData.length,
        data: [],
        aggregateResults: this.aggregateResults,
        errors: null
      };
      // CRITICAL: Return slice of pageData matching the exact skip/take request
      data.data = this.pageData.slice(
        state.skip ?? 0,
        (state.skip ?? 0) + (state.take ?? data.total)
      );
      dataSource.data = this.pageData;

      return of(data);
    }
  }

  /**
   * Initializes the directive, sets up event subscriptions, and performs initial data binding.
   * Overrides the base OnInit implementation.
   */
  public override ngOnInit(): void {
    // Log compatibility mode usage for developer awareness
    if (this.usePageSizePaging && !this.hasLoggedPagingNotice) {
      console.info(
        `[KendoDataSourceMvcDirective] Using page/pageSize paging for compatibility with older server implementations. ` +
        `For modern backends, the default mode (using skip/take) is recommended for virtual scrolling.`
      );
      this.hasLoggedPagingNotice = true;
    }

    // Initialize the base state first
    this.applyState(this.state);
    
    // Initialize local state tracking with current grid values
    this.currentFilter = this.grid.filter ?? undefined;
    this.currentSort = this.grid.sort ?? undefined;
    this.currentGroup = this.grid.group ?? undefined;

    // Track filter/sort/group changes immediately to have the latest state available for other events
    this._subscriptions.push(
      this.grid.filterChange.subscribe((filter) => (this.currentFilter = filter)),
      this.grid.sortChange.subscribe((sort) => (this.currentSort = sort)),
      this.grid.groupChange.subscribe((group) => (this.currentGroup = group))
    );

    // Individual state update streams - these will be merged and batched
    const filterChange$ = this.grid.filterChange.pipe(
      map(filter => ({ filter, skip: 0 })), // Reset pagination when filter changes
      tap(() => {
        // Filter changes invalidate the entire page cache
        this.pageData = [];
        this.serverTotal = 0;
      })
    );

    const sortChange$ = this.grid.sortChange.pipe(
      map(sort => ({ sort, skip: 0 })), // Reset pagination when sort changes
      tap(() => {
        // Sort changes invalidate the entire page cache
        this.pageData = [];
        this.serverTotal = 0;
      })
    );

    const groupChange$ = this.grid.groupChange.pipe(
      map(group => ({ group, skip: 0 })), // Reset pagination when group changes
      tap(() => {
        // Group changes invalidate the entire page cache
        this.pageData = [];
        this.serverTotal = 0;
      })
    );

    // Page changes do not reset the cache
    const pageChange$ = this.grid.pageChange.pipe(
      map(page => ({ skip: page.skip, take: page.take }))
    );

    // Manual rebinds with debouncing to prevent rapid successive calls
    const rebindChange$ = this.rebindTrigger.pipe(
      debounceTime(150), // Debounce rapid rebind calls
      map(() => ({}))
    );

    // Unified stream of all state update events
    const stateUpdate$ = merge(
      filterChange$,
      sortChange$,
      groupChange$,
      pageChange$,
      rebindChange$
    );

    // Core state batching with proper event merging
    const stateChange$ = stateUpdate$;

    // Main data fetching pipeline
    this._subscriptions.push(
      stateChange$.pipe(
        // Update the directive's state with the merged batch of changes
        tap((batchedState: Partial<State>) => {
          // Update state with the merged values from the batched events
          this.state = {
            ...this.state,
            // Use current tracking variables for the latest state
            filter: this.currentFilter,
            sort: this.currentSort,
            group: this.currentGroup,
            // Apply batched state updates (later events override earlier ones)
            ...batchedState,
            // Ensure take has a default value
            take: batchedState.take ?? this.state.take ?? this.grid.pageSize ?? KendoDataSourceMvcDirective.DEFAULT_PAGE_SIZE
          };
        }),
        // switchMap cancels previous pending requests and fetches new data
        switchMap(() => {
          this.grid.loading = true;
          return this.query(this.state).pipe(
            catchError(err => {
              console.error('Failed to fetch grid data:', err);
              this.dataError.emit(err);
              return of({ data: [], total: 0, aggregateResults: null, errors: null } as IMvcGridDataResult);
            })
          );
        })
      ).subscribe({
        next: (result) => {
          this.grid.loading = false;
          this.grid.data = result;
          
          // Update the data source's data property for consistency
          if (this.dataSource) {
            this.dataSource.data = result.data;
          }
          
          // Sync grid properties with the final state
          this.grid.skip = this.state.skip;
          this.grid.sort = this.state.sort;
          this.grid.group = this.state.group;
          this.grid.pageSize = this.state.take;
          
          this.notifyDataChange();
        },
        error: (err) => {
          if (this.grid) {
            this.grid.loading = false;
          }
          this.dataError.emit(err);
        }
      })
    );

    // Initial data load on component initialization
    if (this.kendoGridStateDirective == null && (this.dataSource?.autoBind ?? true)) {
      // Trigger the pipeline for the first time
      this.rebindTrigger.next();
    }
  }



  /**
   * Refreshes the grid data based on the current state.
   * This method queries the data source and updates the grid.
   * Overrides the base rebind implementation.
   */
  public override rebind(): void {
    // Simply trigger the reactive stream in ngOnInit
    this.rebindTrigger.next();
  }

  /**
   * Cleans up resources when the directive is destroyed.
   * Unsubscribes from all subscriptions and completes event emitters.
   * Overrides the base OnDestroy implementation.
   */
  public override ngOnDestroy(): void {
    // Unsubscribe from all tracked subscriptions
    if (this._subscriptions.length > 0) {
      this._subscriptions.forEach(subscription => {
        if (subscription && !subscription.closed) {
          subscription.unsubscribe();
        }
      });
      this._subscriptions = [];
    }


    // Clean up other resources
    this._aggregateResults = null;
    this.aggregateResultsChange.complete();
    this.dataError.complete();
    this.rebindTrigger.complete();

    // Call the base implementation
    super.ngOnDestroy();
  }

  /**
   * Checks if the grid's row height matches the actual row height in the DOM.
   * Warns if there's a mismatch that could affect virtual scrolling.
   */
  ngAfterContentChecked() {
    // Only check once after data is loaded to avoid performance issues
    if (!this.hasCheckedRowHeight && this.grid && this.grid.wrapper && this.grid.data && (this.grid.data as any).data?.length > 0) {
      let gridElement = this.grid.wrapper.nativeElement as HTMLElement;
      let firstTrElement = gridElement.querySelector('.k-grid-content tr') as HTMLElement;

      let loadingMask = gridElement.querySelector('.k-loading-mask') as HTMLElement;
      let skeletonText = gridElement.querySelector('.k-skeleton-text') as HTMLElement;

      if (!loadingMask && !skeletonText && firstTrElement) {
        let height = firstTrElement.offsetHeight; // Get the height of the first row in the grid
        let gridRowHeight = this.grid.rowHeight; // This is the configured row height from the grid options
        if (gridRowHeight !== height) {
          console.warn('Row height mismatch. Virtual scrolling may not work correctly. Configured: ' + gridRowHeight + ' DOM Actual: ' + height);
        }
        this.hasCheckedRowHeight = true; // Mark as checked to prevent running again
      }
    }
  }
}